import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, createSearchParams } from "react-router-dom";
// import { useTranslation } from "react-i18next";
import locationData from "consts/locations.json";
import USFlag from "assets/usFlag.svg";
import DEFlag from "assets/deFlag.svg";
import StoreIcon from "assets/icons/storefront.svg";
import MailIcon from "assets/icons/package.svg";
import ColoradoIcon from "assets/states/colorado.svg";
import NewJerseyIcon from "assets/states/newjersey.svg";
import PennsylvaniaIcon from "assets/states/pennsylvania.svg";
import ArkansasIcon from "assets/states/arkansas.svg";
import { Button } from "components/button";
import { useCurrentLocation, getQueryParams } from "lib/utils";

const flagMap = {
  US: USFlag,
  DE: DEFlag,
};

const stateIconMap = {
  CO: ColoradoIcon,
  NJ: NewJerseyIcon,
  PA: PennsylvaniaIcon,
  AR: ArkansasIcon,
};

const generateLocationUrl = (location, queryParams) => {
  const newQueryParams = { ...queryParams, state: location.state };

  return `/${location.locale}${window.location.pathname.replace(`/en-US`, ``).replace(`/de-DE`, ``)}?${createSearchParams(newQueryParams).toString()}`;
};

export default function LocaleModal({ setModalOpen = () => {}, title }) {
  const [selectedLocale, setSelectedLocale] = useState(null);
  const location = useLocation();
  const queryParams = getQueryParams(location.search);

  const { location: currentLocation } = useCurrentLocation();

  useEffect(() => {
    if (!selectedLocale && currentLocation.locale) {
      setSelectedLocale(generateLocationUrl(currentLocation, queryParams));
    }
  }, [currentLocation, queryParams, selectedLocale]);

  // const { t } = useTranslation();

  return (
    <div className="fixed w-screen h-screen top-0 left-0 z-50 flex items-center region-modal">
      <motion.div
        className="bg-[#000000b6] w-full h-[100dvh] fixed top-0 left-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={() => setModalOpen(false)}
      ></motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
        className="bg-white scrollbar-hide rounded-3xl w-[min(500px,90vw)] mx-auto p-6 relative translate-y-[-50px] md:translate-y-0 max-h-[90vh] flex flex-col shadow-[0px_16px_24px_-12px_rgba(6,14,35,0.16),0px_0px_0px_1px_rgba(6,14,35,0.04),0px_0px_16px_4px_rgba(6,14,35,0.08)]"
      >
        <h1 className="text-[26px] font-bold leading-[29px] mb-6">
          Select your region
        </h1>
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex flex-col gap-[4px]">
            {locationData
              // .filter((loc) => loc.state == null)
              .map((location, index) => {
                const isGenericUS =
                  location.state === null && location.country === "US";
                const getLocationName = () => {
                  if (isGenericUS) {
                    return "United States, All other locations";
                  }
                  // Extract the area name from modalName (e.g., "United States - Denver, CO area" -> "Denver, CO area")
                  const areaMatch =
                    location.modalName.match(/United States - (.+)/);
                  return areaMatch ? areaMatch[1] : location.modalName;
                };

                return (
                  <div
                    key={index}
                    className={`flex items-center gap-6 p-4 ${
                      selectedLocale ===
                      generateLocationUrl(location, queryParams)
                        ? "bg-[#BBDCF53D]"
                        : "bg-white hover:bg-gray-300/10"
                    } rounded-2xl cursor-pointer transition-colors`}
                    onClick={() =>
                      setSelectedLocale(
                        generateLocationUrl(location, queryParams)
                      )
                    }
                  >
                    <div className="w-18 h-12 flex-shrink-0 aspect-[4/3]">
                      <img
                        src={
                          location.state && stateIconMap[location.state]
                            ? stateIconMap[location.state]
                            : flagMap[location.country]
                        }
                        alt={`${location.state || location.country} icon`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                      <div className="text-[18px] leading-[23px] text-black">
                        {getLocationName()}
                      </div>
                      <div className="flex gap-4 items-center">
                        {location.takebackMethod.includes("mail") && (
                          <div className="flex gap-1 items-center">
                            <img
                              src={MailIcon}
                              alt="Mail icon"
                              className="w-5 h-5"
                            />
                            <span className="text-base text-black-400">
                              Mail-In
                            </span>
                          </div>
                        )}
                        {location.takebackMethod.some((method) =>
                          /in_store/.test(method)
                        ) && (
                          <div className="flex gap-1 items-center">
                            <img
                              src={StoreIcon}
                              alt="Store icon"
                              className="w-5 h-5"
                            />
                            <span className="text-base text-black-400">
                              In-Store
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div className="flex justify-end pt-6">
          <Button
            onClick={() => window.location.assign(selectedLocale)}
            className="bg-blue hover:bg-blue-600 text-white font-bold px-6 py-3 rounded-[100px] min-w-[120px]"
          >
            Continue
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
